import { Component, Inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MenuServiceService } from '../../menu-service/menu-service.service';
import { AdditionalServiceFormComponent } from '../additional-service-form/additional-service-form.component';
import { Router } from '@angular/router';
import { Fmt } from 'src/app/core/messages/fmt';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Utils } from 'src/app/core/resources/utils';
import { AdditionalCostCargo } from 'src/app/core/interfaces/additionalCostCargo';
import { NgxSpinnerService } from 'ngx-spinner';
import { CargoDetailService } from 'src/app/modules/cargo/cargo-detail/cargo-detail.service';
import { BasicResponse } from 'src/app/core/interfaces/basicResponse';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';
import { TravelExpensesService } from 'src/app/core/services/travel-expenses.service';
import { TravelConcept, TravelExpense } from 'src/app/core/interfaces/travel-expense';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/authentication.service';
import { AmountsCargoEnum } from 'src/app/core/enums/amountsCargo.enum';
import { CompanyManager } from 'src/app/core/managers/company.manager';
import { CurrencyMaskDirective } from 'src/app/core/directives/currency-mask.directive';
import { delay } from 'rxjs/operators';
import { ShippingCost as ShippingCostEnum } from 'src/app/core/enums/shipping-cost.enum';
import { ServiceResources } from '../../resources/services';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { SettingId } from 'src/app/core/interfaces/saas-config';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { CargoItemService } from 'src/app/modules/cargo/cargo-item/cargo-item.service';
import { CargoPaymentsService } from 'src/app/core/services/cargo-payments.service';

@Component({
  selector: 'app-additional-values-service-form',
  templateUrl: './additional-values-service-form.component.html',
  styleUrls: ['./additional-values-service-form.component.scss']
})
export class AdditionalValuesServiceFormComponent implements OnInit {
  @ViewChild(AdditionalServiceFormComponent) additionalServiceForm: AdditionalServiceFormComponent;
  @ViewChildren(CurrencyMaskDirective) currencyMasks: QueryList<CurrencyMaskDirective>;
  permission = Permission;

  rateControl = new FormControl(0, [Validators.required, Validators.min(this.hasExceptionRateOrCost ? 0 : 1)]);
  freightCostControl = new FormControl(0, [Validators.required, Validators.min(this.hasExceptionRateOrCost ? 0 : 1)]);
  skipFreightRNDCReportControl = new FormControl(false);
  requiresPreBillControl = new FormControl(false);
  advancePercentageControl = new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]);
  valueAdvanceControl = new FormControl(0, [Validators.required, Validators.min(0)]);
  paymentTimeControl = new FormControl(null, [Validators.required]);
  otherPaymentTimeControl = new FormControl(3, [Validators.required, Validators.min(1)]);
  paymentTimeValues = [8, 15, 30, 60];

  travelExpensesSaved: FormArray = new FormArray([]);
  originalTravelExpenses: TravelExpense[] = [];
  concepts: Array<TravelConcept> = [];
  maxPercentage: number = 100;
  minUtility: number = ShippingCostEnum.MINIMUN_UTILITY;

  formSubscription: Subscription;
  advancePercentageControlSubscription: Subscription;
  freightCostControlSubscription: Subscription;
  valueAdvanceControlSubscription: Subscription;

  isDialog: boolean = false;
  maximumFreightCostLimit: number = 0;
  minimumFreightCostLimit: number = 1;
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public paramsDialog: { cargo: Cargo },
    public dialogRef: MatDialogRef<AdditionalValuesServiceFormComponent>,
    public menuService: MenuServiceService,
    private router: Router,
    private formBuilder: FormBuilder,
    private snackBarService: SnackBarService,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private cargoDetailService: CargoDetailService,
    private cargoItemService: CargoItemService,
    private travelExpensesService: TravelExpensesService,
    private permissionRole: PermissionRole,
    private authService: AuthService,
    private companyManager: CompanyManager,
    private serviceResources: ServiceResources,
    public cargoPaymentsService: CargoPaymentsService
  ) { }

  async ngOnInit() {
    await this.checkDialogParams();
    this.getConcepts();
    this.setLocalValues();
    this.setSubscription();
  }

  private async checkDialogParams() {
    if (this.paramsDialog && this.paramsDialog.cargo) {
      this.isDialog = true;
      this.menuService.cargo = this.paramsDialog.cargo;
      this.menuService.patchForm(this.paramsDialog.cargo, false);
      await this.menuService.setCargoConsignments();
      await this.menuService.setCargoVehicle();
    }
  }
  /**
   * @description Gets travel expense concepts from service
   */
  private getConcepts() {
    this.travelExpensesService.getTypes().subscribe(
      (concepts: TravelConcept[]) => this.concepts = concepts && concepts.length ? concepts : [],
      () => this.concepts = []
    );
  }

  /**
   * @description Set initial values for the form
   */
  private setLocalValues() {
    const freightCostValidators = [Validators.required];
    if (this.canReduceFreightCostInScheduledTransportService) {
      this.maximumFreightCostLimit = this.getMaximumFreightCostLimit();
      freightCostValidators.push(Validators.max(this.maximumFreightCostLimit));
      this.minimumFreightCostLimit = this.getMinimumFreightCostLimit();
      freightCostValidators.push(Validators.min(this.minimumFreightCostLimit));
    } else {
      this.minimumFreightCostLimit = this.hasExceptionRateOrCost ? 0 : 1;
      freightCostValidators.push(Validators.min(this.minimumFreightCostLimit));
    }
    this.rateControl.setValidators([Validators.required, Validators.min(this.hasExceptionRateOrCost ? 0 : 1)]);
    this.freightCostControl.clearValidators();
    this.freightCostControl.setValidators(freightCostValidators);
    this.rateControl.setValue(
      this.menuService.form.get('shippingCost.rate').value ? Number(this.menuService.form.get('shippingCost.rate').value) : 0
    );
    this.freightCostControl.setValue(
      this.menuService.form.get('shippingCost.freightCost').value ? Number(this.menuService.form.get('shippingCost.freightCost').value) : 0
    );

    this.skipFreightRNDCReportControl.setValue(
      this.menuService.form.get('shippingCost.skipFreightRNDCReport').value ? this.menuService.form.get('shippingCost.skipFreightRNDCReport').value : false
    );
    this.requiresPreBillControl.setValue(this.menuService.form.get('requiresPreBill').value);
    this.minUtility = this.menuService.form.get('idCompany').value ? this.companyManager.getUtilityByCompanyId(this.menuService.form.get('idCompany').value) : ShippingCostEnum.MINIMUN_UTILITY;

    const companyPercentage = this.menuService.form.get('idCompany').value ? this.companyManager.getAdvancePercentageByCompanyId(this.menuService.form.get('idCompany').value) : null;
    if (this.menuService.isTransportService()) {
      this.maxPercentage = this.utils.isDefined(companyPercentage) ? companyPercentage :
        this.authService.getCompany().companyId && (this.authService.getCompany().companyId === environment.rootNit)
          ? AmountsCargoEnum.MAX_ADVANCE_PERCENTAGE_ALLOWED_TECLOGI
          : AmountsCargoEnum.MAX_ADVANCE_PERCENTAGE_ALLOWED_ALL_COMPANIES;
    }

    const advancePercentage = this.menuService.form.get('shippingCost.advancePercentage').value;
    this.advancePercentageControl.setValue(
      advancePercentage ? (typeof advancePercentage === 'number' && advancePercentage <= this.maxPercentage ? advancePercentage : this.maxPercentage) : 0
    );
    this.advancePercentageControl.setValidators([Validators.required, Validators.min(0), Validators.max(this.maxPercentage)]);
    const advanceValue = this.menuService.form.get('shippingCost.valueAdvance').value;
    this.valueAdvanceControl.setValue(
      advanceValue ? (typeof advanceValue === 'number' && advanceValue <= this.maxAdvanceValue ? advanceValue : this.maxAdvanceValue) : 0
    );

    const paymentTime = this.menuService.form.get('shippingCost.paymentTime').value;
    if (this.utils.isDefined(paymentTime) && typeof paymentTime === 'number') {
      this.paymentTimeControl.setValue(this.paymentTimeValues.includes(paymentTime) ? paymentTime : -1);
      this.otherPaymentTimeControl.setValue(paymentTime);
    } else {
      this.paymentTimeControl.setValue(null);
      this.otherPaymentTimeControl.setValue(3);
    }
    this.getServiceTravelExpenses();
    this.checkPermissionsToEdit();
  }

  private async getServiceTravelExpenses() {
    this.travelExpensesSaved.clear();
    let travelExpenses: TravelExpense[] = [];
    try {
      travelExpenses = await this.travelExpensesService.byCargoId(this.menuService.cargo.id).toPromise();
    } catch (error) { }
    this.originalTravelExpenses = travelExpenses && travelExpenses.length ? travelExpenses : [];
    this.travelExpensesSaved = this.formBuilder.array(this.originalTravelExpenses.map((travelExpense) => this.formBuilder.group(travelExpense)));
  }

  private setSubscription() {
    this.formSubscription = this.menuService.formObservable.subscribe(() => this.setLocalValues());
    this.advancePercentageControlSubscription = this.advancePercentageControl.valueChanges.pipe(delay(10)).subscribe(() => this.updateValueAdvance());
    this.freightCostControlSubscription = this.freightCostControl.valueChanges.pipe(delay(10)).subscribe(() => this.updateValueAdvance());
    this.valueAdvanceControlSubscription = this.valueAdvanceControl.valueChanges.pipe(delay(10)).subscribe(() => this.updateAdvancePercentage());
  }

  private checkPermissionsToEdit(): void {
    if (!this.canEditFinancialInformation) {
      this.rateControl.disable();
      if (!this.canReduceFreightCostInScheduledTransportService || !this.isDialog)
        this.freightCostControl.disable();
      this.skipFreightRNDCReportControl.disable();
      this.requiresPreBillControl.disable();
      this.advancePercentageControl.disable();
      this.valueAdvanceControl.disable();
      this.paymentTimeControl.disable();
      this.otherPaymentTimeControl.disable();
    }
    if (this.isDialog) {
      this.rateControl.disable();
      if (!this.canReduceFreightCostInScheduledTransportService)
        this.freightCostControl.disable();
      if (![CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED].includes(this.menuService.cargo.state)) {
        this.paymentTimeControl.disable();
        this.otherPaymentTimeControl.disable();
      }
      const advanceState = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.advanceState');
      const advancePercentage = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.advancePercentage');
      if (advanceState && advancePercentage) {
        this.advancePercentageControl.disable();
        this.valueAdvanceControl.disable();
      }
      const hasManifest = this.utils.getNestedValue(this.menuService.cargo, 'manifestAuthorization');
      if (hasManifest)
        this.skipFreightRNDCReportControl.disable();
      const isCashed = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.cashed');
      const hasPreBill = this.utils.getNestedValue(this.menuService.cargo, 'preBill');
      if (isCashed || hasPreBill)
        this.requiresPreBillControl.disable();
    }

  }

  private updateValueAdvance() {
    const freightCost = this.freightCostControl.value || 0;
    const percentage = parseFloat(Number(this.advancePercentageControl.value).toFixed(1)) || 0;
    const valueAdvance = parseFloat(((freightCost * percentage) / 100).toFixed(1));
    this.valueAdvanceControl.setValue(valueAdvance, { emitEvent: false });
    const valueAdvanceMask = this.currencyMasks.find(mask => mask.maskId === 'valueAdvance');
    if (valueAdvanceMask)
      valueAdvanceMask.formatValue(valueAdvance);
  }
  private updateAdvancePercentage() {
    const freightCost = this.freightCostControl.value || 0;
    const valueAdvance = parseFloat(Number(this.valueAdvanceControl.value).toFixed(1)) || 0;
    const percentage = parseFloat(((valueAdvance * 100) / freightCost).toFixed(1)) || 0;
    this.advancePercentageControl.setValue(percentage, { emitEvent: false });
  }

  compareTravelExpensesType(a: TravelConcept, b: TravelConcept) {
    return a.id === b.id;
  }

  addTravelExpense() {
    this.travelExpensesSaved.push(this.menuService.createTravelExpenseGroup());
  }

  removeTravelExpense(index: number) {
    this.travelExpensesSaved.removeAt(index);
  }

  get hasExceptionRateOrCost(): boolean {
    const companyOwnFleet = this.authService && this.authService.hasCompanySettingEnabled(SettingId.OWN_FLEET);
    return companyOwnFleet || this.menuService.hasPermissionNoMinimumUtility();
  }

  get canSeeFinancialInformation(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.showFinancialInformationModule
    );
  }

  get canEditFinancialInformation(): boolean {
    return this.canSeeFinancialInformation &&
      this.permissionRole.hasPermission(
        this.permission.cargo.module,
        this.permission.cargo.showEditFinancialInformationModule
      );
  }

  get canEditAndSeeTravelExpenses(): boolean {
    return this.menuService.isTransportService() &&
      this.authService.hasCompanySettingEnabled(SettingId.USE_TRAVEL_EXPENSES) &&
      this.permissionRole.hasPermission(
        this.permission.cargo.module,
        this.permission.cargo.editTravelExpenses
      ) && !this.isDialog;
  }

  get canReduceFreightCostInScheduledTransportService(): boolean {
    return [CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED].includes(this.menuService.cargo.state) && this.getMaximumFreightCostLimit() > 0 &&
      this.permissionRole.hasPermission(
        this.permission.cargo.module,
        this.permission.cargo.reduceScheduledTransportFreightCost
      );
  }

  get canDeleteTravelExpenses(): boolean {
    return this.canEditAndSeeTravelExpenses &&
      this.permissionRole.hasPermission(
        this.permission.cargo.module,
        this.permission.cargo.removeTravelExpenses
      );
  }

  get maxAdvanceValue(): number {
    const freightCost = this.freightCostControl.value || 0;
    return freightCost * this.maxPercentage / 100;
  }

  get showAdditionalCosts(): boolean {
    const isSomeAdditionalCost = this.menuService.form.get('additionalCosts').value && this.menuService.form.get('additionalCosts').value.length;
    const hasPermissionToAdd = this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.additionalCostCreate
    );
    return this.canEditAdditionalCosts && (isSomeAdditionalCost || hasPermissionToAdd) && !this.isDialog;
  }

  private get canEditAdditionalCosts(): boolean {
    return this.menuService.isTransportService() &&
      this.permissionRole.hasPermission(
        this.permission.cargo.module,
        this.permission.cargo.additionalCosts
      );
  }

  get utilityCargo(): number {
    if (!this.rateControl.value) return 0;
    const utilityValue = (this.rateControl.value - this.freightCostControl.value) * 100 / this.rateControl.value;
    return !Number.isNaN(utilityValue) ? utilityValue : 0;
  }

  get utilityAlertClass(): string {
    if (!this.minUtility || this.hasExceptionRateOrCost) return 'alert-primary';
    if (this.utilityCargo === this.minUtility) return 'alert-primary';
    if (this.utilityCargo > this.minUtility) return 'alert-success';
    if (this.utilityCargo < this.minUtility) return 'alert-danger';
    return 'alert-primary';
  }

  get messageAdvancePercentageAllowed(): string {
    return `El porcentaje máximo permitido ${this.menuService.form.get('idCompany').value
      && this.companyManager.getNameByCompanyId(this.menuService.form.get('idCompany').value) !== this.menuService.form.get('idCompany').value
      ? `por ${this.companyManager.getNameByCompanyId(this.menuService.form.get('idCompany').value)}`
      : ''} es ${this.maxPercentage}%`;
  }

  backToPreviousStep() {
    if (this.menuService.isSatelliteLockService())
      return this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.satelliteLockService, this.menuService.cargo.consecutive)]);
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.loadCharacteristics, this.menuService.cargo.consecutive)]);
  }

  private isValidForm(): boolean {
    if (this.utils.errorMessagesCustomized(this.freightCostControl, 'flete', null, null, this.minimumFreightCostLimit, this.maximumFreightCostLimit)) return false;
    if (this.utils.errorMessagesCustomized(this.advancePercentageControl, 'porcentaje de anticipo', null, null, 0, this.maxPercentage)) return false;
    if (this.utils.errorMessagesCustomized(this.valueAdvanceControl, 'valor del anticipo', null, null, 0, this.maxAdvanceValue)) return false;
    if (this.utils.errorMessagesCustomized(this.paymentTimeControl, 'tiempo de pago')) return false;
    if (this.paymentTimeControl.value === -1 && this.utils.errorMessagesCustomized(this.otherPaymentTimeControl, 'tiempo de pago', null, null, 1)) return false;
    return true;
  }



  private async updateReducedFreightCost(nextFreightCost: number): Promise<boolean> {
    this.spinner.show();
    try {
      await this.cargoDetailService.reduceFreightCost(this.menuService.cargo.id, nextFreightCost).toPromise();
      this.spinner.hide();
      return true;
    } catch (error) {
      this.spinner.hide();
      this.snackBarService.openSnackBar('Ocurrió un error al actualizar el valor del flete', undefined, 'error');
      return false;
    }
  }

  async saveAndContinue() {
    if (this.isDialog && !this.isValidForm()) return;
    if (this.minUtility && this.utilityCargo < this.minUtility && !this.hasExceptionRateOrCost)
      return this.snackBarService.openSnackBar(Fmt.string(FormMessages.MINIMUN_UTILITY_NOT_REACHED, this.minUtility), undefined, 'alert');
    if (this.freightCostControl.value < 0)
      return this.snackBarService.openSnackBar("El valor del flete no puede ser negativo", undefined, 'alert');
    if (this.utils.errorMessagesCustomized(this.advancePercentageControl, 'porcentaje de anticipo', null, null, 0, this.maxPercentage)) return;
    if (this.utils.errorMessagesCustomized(this.valueAdvanceControl, 'valor del anticipo', null, null, 0, this.maxAdvanceValue)) return;
    if (this.valueAdvanceControl.value > this.maxAdvanceValue)
      return this.snackBarService.openSnackBar(Fmt.string(FormMessages.MAX_VALUE_EXCEED, 'valor del anticipo', this.maxAdvanceValue), undefined, 'alert');

    const rateValue = this.utils.clone(this.rateControl.value);
    const rawFreightCostValue = this.utils.clone(this.freightCostControl.value);
    const freightCostValue = rawFreightCostValue ? Number(rawFreightCostValue) : 0;
    const skipFreightRNDCReportValue = this.utils.clone(this.skipFreightRNDCReportControl.value);
    const requiresPreBillValue = this.utils.clone(this.requiresPreBillControl.value);
    const integralValuesPercentage = this.utils.getNestedValue(this.menuService.form.value, 'shippingCost.integralValuesPercentage');
    const advancePercentageValue = this.advancePercentageControl.value
      ? this.advancePercentageControl.value <= this.maxPercentage
        ? this.utils.clone(this.advancePercentageControl.value)
        : this.utils.clone(this.maxPercentage)
      : 0;
    let valueAdvanceValue = this.valueAdvanceControl.value
      ? this.valueAdvanceControl.value <= this.maxAdvanceValue
        ? this.utils.clone(this.valueAdvanceControl.value)
        : this.utils.clone(this.maxAdvanceValue)
      : 0;
    const paymentTimeValue: number = this.paymentTimeControl.value === -1
      ? this.otherPaymentTimeControl.value ? Number(this.utils.clone(this.otherPaymentTimeControl.value)) : 0
      : this.paymentTimeControl.value ? this.utils.clone(this.paymentTimeControl.value) : 0;
    if (paymentTimeValue && paymentTimeValue > 60 && !!this.menuService.cargo.ministry)
      return this.snackBarService.openSnackBar(Fmt.string(FormMessages.MAX_VALUE_EXCEED, 'tiempo de pago', 60), undefined, 'alert');

    if (this.canReduceFreightCostInScheduledTransportService && freightCostValue < this.maximumFreightCostLimit) {
      const freightCostWasUpdated = await this.updateReducedFreightCost(freightCostValue);
      if (!freightCostWasUpdated) return;
      await this.menuService.refreshCargo();
    }
    if (this.travelExpensesSaved.value && this.canEditAndSeeTravelExpenses) {
      const { success } = await this.processTravelExpenses();
      if (!success) return;
    }

    if (this.menuService.isTransportService() && this.additionalServiceForm) {
      const additionalServiceForm = this.additionalServiceForm.additionalCostsArray;
      if (additionalServiceForm.value.length) {
        const { success } = await this.createAdditionalCosts(additionalServiceForm);
        if (success) await this.menuService.refreshCargo();
        else return;
      }
    }
    this.menuService.form.get('shippingCost.rate').setValue(rateValue);
    if (this.menuService.form.get('shippingCost.initialRate'))
      this.menuService.form.get('shippingCost.initialRate').setValue(rateValue);
    this.menuService.form.get('shippingCost.freightCost').setValue(freightCostValue);
    this.menuService.form.get('shippingCost.totalCost').setValue(freightCostValue);

    if (this.menuService.cargo.state === CargoStateEnum.REQUEST) {
      if (integralValuesPercentage)
        this.menuService.form.get('shippingCost.integralValue').setValue(freightCostValue * integralValuesPercentage / 100);
    } else {
      const bonuses = this.cargoPaymentsService.getTotalBonuses(this.menuService.cargo);
      const discounts = this.cargoPaymentsService.getTotalDiscounts(this.menuService.cargo);
      if (bonuses > 0 || discounts > 0)
        valueAdvanceValue = parseFloat((((freightCostValue + bonuses - discounts) * advancePercentageValue) / 100).toFixed(1));
    }
    this.menuService.form.get('shippingCost.advancePercentage').setValue(advancePercentageValue);
    this.menuService.form.get('shippingCost.valueAdvance').setValue(valueAdvanceValue);
    this.menuService.form.get('shippingCost.paymentTime').setValue(paymentTimeValue);
    this.menuService.form.get('shippingCost.skipFreightRNDCReport').setValue(skipFreightRNDCReportValue);
    this.menuService.form.get('requiresPreBill').setValue(requiresPreBillValue);
    this.menuService.updateService().then(() => {
      this.snackBarService.openSnackBar('Servicio actualizado exitosamente');
      if (!this.isDialog) this.continueToNextStep();
      else this.dialogRef.close({ state: true });
    }).catch(() => {
      this.snackBarService.openSnackBar('Ocurrió un error al actualizar el servicio', undefined, 'error');
    });
  }

  private getMinimumFreightCostLimit(): number {
    const minimumFreightCost = 1;
    const bonuses = this.cargoPaymentsService.getTotalBonuses(this.menuService.cargo);
    const discounts = this.cargoPaymentsService.getTotalDiscounts(this.menuService.cargo);
    const limit = minimumFreightCost + discounts - bonuses;
    return limit >= minimumFreightCost ? limit : minimumFreightCost;
  }

  private getMaximumFreightCostLimit(): number {
    const totalCost = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.totalCost');
    if (totalCost) return totalCost;
    return 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  }

  continueToNextStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.observationsService, this.menuService.cargo.consecutive)]);
  }

  private async processTravelExpenses(): Promise<{ success: boolean }> {
    const travelExpensesSaved = this.travelExpensesSaved.value;
    const originalTravelExpenses = this.originalTravelExpenses;
    const travelExpensesToAdd = travelExpensesSaved.filter((travelExpense) => !travelExpense.id);
    const travelExpensesToRemove = originalTravelExpenses.filter((travelExpense) => !travelExpensesSaved.some((savedTravelExpense) => savedTravelExpense.id === travelExpense.id));
    const travelExpensesToUpdate = travelExpensesSaved.filter((travelExpense) => travelExpense.id).filter((travelExpense) => {
      const originalTravelExpense = originalTravelExpenses.find((originalTravelExpense) => originalTravelExpense.id === travelExpense.id);
      return originalTravelExpense && (originalTravelExpense.totalPaid !== travelExpense.totalPaid || originalTravelExpense.travelExpensesType.id !== travelExpense.travelExpensesType.id);
    });

    const promises = [];
    promises.push(...await this.addTravelExpenses(travelExpensesToAdd));
    promises.push(...await this.removeTravelExpenses(travelExpensesToRemove));
    promises.push(...await this.updateTravelExpenses(travelExpensesToUpdate));
    if (!promises.length) return { success: true };

    this.spinner.show();
    try {
      await Promise.all(promises);
      this.spinner.hide();
      return { success: true };
    } catch (error) {
      this.snackBarService.openSnackBar('Uno o varios viáticos no se han guardado correctamente.', undefined, 'error');
      this.spinner.hide();
      return { success: false };
    }
  }

  private async addTravelExpenses(travelExpenses: TravelExpense[]): Promise<BasicResponse[]> {
    const addPromises = [];
    for (const travelExpense of travelExpenses) {
      addPromises.push(this.travelExpensesService.createTravelExpense(travelExpense).toPromise());
    }
    return addPromises;
  }

  private async removeTravelExpenses(travelExpenses: TravelExpense[]): Promise<BasicResponse[]> {
    const removePromises = [];
    for (const travelExpense of travelExpenses) {
      removePromises.push(this.travelExpensesService.removeTravelExpense(travelExpense.id).toPromise());
    }
    return removePromises;
  }

  private async updateTravelExpenses(travelExpenses: TravelExpense[]): Promise<BasicResponse[]> {
    const updatePromises = [];
    for (const travelExpense of travelExpenses) {
      updatePromises.push(this.travelExpensesService.updateTravelExpense(travelExpense.id, travelExpense).toPromise());
    }
    return updatePromises;
  }

  private async createAdditionalCosts(additionalCostsArray: FormArray): Promise<{ success: boolean }> {
    let error = '';
    for (const additionalCost of additionalCostsArray.controls) {
      if (additionalCost.enabled && !additionalCost.valid) {
        const index = additionalCostsArray.controls.indexOf(additionalCost);
        if (additionalCost.errors) error = FormMessages.MINIMUN_UTILITY_NOT_REACHED_DEFAULT + ` en el servicio ${index + 1}`;
        else error = Fmt.string(FormMessages.MISSING_FIELD, `tarifa del servicio ${index + 1}`);
        break;
      }
    }
    if (error) {
      this.snackBarService.openSnackBar(error, undefined, 'alert');
      return { success: false };
    }
    const formValue: AdditionalCostCargo[] = additionalCostsArray.value;
    const additionalCostsToCreate = formValue.filter((additionalCost) => !additionalCost.id);
    if (!additionalCostsToCreate.length) return { success: true };
    const body: AdditionalCostCargo[] = this.utils.clone(additionalCostsToCreate).map((additionalCost) => {
      delete additionalCost.type.disabled;
      delete additionalCost.type.whoToPaysUtilities;
      delete additionalCost.type.utility;
      delete additionalCost.type.utilityByActor;
      return additionalCost;
    });
    this.spinner.show();
    let success: BasicResponse;
    try { success = await this.cargoDetailService.addAdditionalcost(this.menuService.cargo.id, body).toPromise() } catch (error) { }
    this.spinner.hide();
    if (!success || !success.message || success.message !== 'Update') {
      this.snackBarService.openSnackBar('Ocurrió un error al agregar los servicios adicionales', undefined, 'error');
      return { success: false };
    }
    this.snackBarService.openSnackBar('Servicios adicionales registrados correctamente');
    return { success: true };
  }

  ngOnDestroy() {
    if (this.formSubscription) this.formSubscription.unsubscribe();
    if (this.advancePercentageControlSubscription) this.advancePercentageControlSubscription.unsubscribe();
    if (this.freightCostControlSubscription) this.freightCostControlSubscription.unsubscribe();
    if (this.valueAdvanceControlSubscription) this.valueAdvanceControlSubscription.unsubscribe();
  }
}
